/**
 * QueryClient de TanStack Query.
 *
 * Decisiones que importan acá:
 * - **No se reintenta un 401/403/404**: un permiso denegado no mejora repitiendo, y cada
 *   reintento sería otra línea de «consulta sensible denegada» en la auditoría.
 * - `staleTime` corto pero no cero: las pantallas operativas se refrescan al volver a la
 *   ventana, no cada render.
 */
import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "../api.js";

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry(failureCount, error) {
          if (error instanceof ApiError) {
            if ([400, 401, 403, 404, 409, 422].includes(error.status)) return false;
          }
          return failureCount < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

/** Claves de query. Centralizadas para que invalidar sea exacto y no «por las dudas». */
export const qk = {
  me: ["me"] as const,
  branches: ["branches"] as const,
  locations: (branchId?: string | null) => ["locations", branchId ?? null] as const,
  rooms: (branchId?: string | null) => ["rooms", branchId ?? null] as const,
  accessPoints: (branchId?: string | null) => ["access-points", branchId ?? null] as const,
  disciplines: ["disciplines"] as const,
  users: (filters: unknown) => ["users", filters] as const,
  user: (id: string) => ["users", id] as const,
  userSessions: (id: string) => ["users", id, "sessions"] as const,
  mySessions: ["me", "sessions"] as const,
  myTimeclockQr: ["me", "timeclock-qr"] as const,
  auditLogs: (filters: unknown) => ["audit-logs", filters] as const,
  auditLog: (id: string) => ["audit-logs", id] as const,
  securityAlerts: ["security-alerts"] as const,
  securityAlertHistory: (alertKey?: string) =>
    ["security-alerts", "history", alertKey ?? null] as const,
  settings: (branchId?: string | null) => ["settings", branchId ?? null] as const,
  settingHistory: (key: string, branchId?: string | null) =>
    ["settings", key, "history", branchId ?? null] as const,
  catalogs: ["catalogs"] as const,
  devices: (filters: unknown) => ["devices", filters] as const,
  jobs: ["jobs"] as const,
  health: ["health"] as const,
  dashboard: (range: unknown) => ["reports", "dashboard", range] as const,

  // ── Integraciones con terceros (`docs/api/integraciones.md`) ──────────────
  // El detalle cuelga del listado a propósito: guardar, probar o revocar cambian la fila
  // Y el detalle, así que invalidar `["integrations"]` refresca las dos de una.
  integrations: ["integrations"] as const,
  // Emisores fiscales de ARCA: la lista trae emisores + sedes; todo invalida la lista.
  fiscalEmitters: ["fiscal-emitters"] as const,
  fiscalEmitterIncidents: ["fiscal-emitters", "incidents"] as const,
  integration: (key: string) => ["integrations", key] as const,
  // Webhooks salientes (Slack): guardar o probar cambian la fila; se invalida la lista.
  webhooks: ["webhooks"] as const,

  // ── §12 Reportes y alertas · §9.2 Resultado por sede y actividad ─────────
  // Todas cuelgan de `["reports", …]`: invalidar la raíz alcanza para refrescar el
  // módulo entero cuando cambia un parámetro que mueve TODOS los números (el prorrateo
  // de Central, el criterio de comunes por actividad).
  reportDefaults: ["reports", "defaults"] as const,
  billing: (filters: unknown) => ["reports", "billing", filters] as const,
  billingPayments: (filters: unknown) => ["reports", "billing", "payments", filters] as const,
  monthlyRevenue: (filters: unknown) => ["reports", "monthly-revenue", filters] as const,
  activeMembers: (filters: unknown) => ["reports", "active-members", filters] as const,
  activeMembersSeries: (filters: unknown) =>
    ["reports", "active-members", "series", filters] as const,
  attendanceReport: (filters: unknown) => ["reports", "attendance", filters] as const,
  goalsReport: (filters: unknown) => ["reports", "goals", filters] as const,
  goalsScope: ["reports", "goals", "scope"] as const,
  atRisk: (filters?: unknown) => ["retention", "at-risk", filters ?? null] as const,
  retentionHistory: (memberId: string) => ["retention", "members", memberId, "contacts"] as const,
  cashClosuresReport: (filters: unknown) => ["reports", "cash-closures", filters] as const,
  branchResults: (month: string) => ["reports", "branch-results", month] as const,
  activityProfitability: (filters: unknown) =>
    ["reports", "activity-profitability", filters] as const,
  businessAlertsConfig: ["reports", "alerts", "config"] as const,
  businessAlertsHistory: (filters: unknown) => ["reports", "alerts", "history", filters] as const,
  reportAggregates: (filters: unknown) => ["reports", "aggregates", filters] as const,

  // ── §3 Socios ─────────────────────────────────────────────────────────────
  members: (filters: unknown) => ["members", filters] as const,
  member: (id: string) => ["members", id] as const,
  checkIdentity: (docType: string, docNumber: string) =>
    ["members", "check-identity", docType, docNumber] as const,
  aptStatus: (personId: string) => ["people", personId, "apt-status"] as const,
  medicalCertificates: (personId: string) => ["people", personId, "certificates"] as const,
  signedDocuments: (personId: string) => ["people", personId, "signed-documents"] as const,
  legalDocVersions: (filters: unknown) => ["legal-doc-versions", filters] as const,
  signRequest: (requestId: string) => ["kiosk", "sign-requests", requestId] as const,
  kioskStaff: ["kiosk", "staff"] as const,
  familyGroups: (filters: unknown) => ["family-groups", filters] as const,
  familyGroup: (id: string) => ["family-groups", id] as const,
  corporateAgreements: (filters: unknown) => ["corporate-agreements", filters] as const,
  benefits: (filters: unknown) => ["benefits", filters] as const,
  courtesyReport: (filters: unknown) => ["benefits", "courtesy-report", filters] as const,
  visitors: (filters: unknown) => ["visitors", filters] as const,
  visitor: (id: string) => ["visitors", id] as const,
  visitorEntries: (filters: unknown) => ["visitors", "entries", filters] as const,
  emergencyWithdrawals: (filters: unknown) => ["emergency-withdrawals", filters] as const,
  emergencyWithdrawal: (id: string) => ["emergency-withdrawals", id] as const,
  mergeCandidates: (filters: unknown) => ["members-merge", "candidates", filters] as const,

  // ── §4 Planes, precios y promociones ──────────────────────────────────────
  plans: (filters: unknown) => ["plans", filters] as const,
  plansWithPrices: ["plans", "with-prices"] as const,
  priceLists: (filters: unknown) => ["price-lists", filters] as const,
  priceListCurrent: ["price-lists", "current"] as const,
  priceListHistory: ["price-lists", "history"] as const,
  promotions: (filters: unknown) => ["promotions", filters] as const,
  promotionUsage: (filters: unknown) => ["promotions", "usage", filters] as const,
  // Flyers de precios: el render cuelga del flyer, así guardar refresca la vista previa.
  flyers: (filters: unknown) => ["flyers", "list", filters] as const,
  flyer: (id: string) => ["flyers", id] as const,
  flyerRender: (id: string) => ["flyers", id, "render-data"] as const,
  flyerPreview: (config: unknown) => ["flyers", "preview", config] as const,
  flyerAssets: ["flyers", "assets"] as const,

  // ── §4 Cobranzas ──────────────────────────────────────────────────────────
  memberAccount: (memberId: string, filters?: unknown) =>
    ["member-account", memberId, filters ?? null] as const,
  memberSearch: (q: string) => ["member-search", q] as const,
  charges: (filters: unknown) => ["charges", filters] as const,
  debtors: (filters: unknown) => ["debtors", filters] as const,
  debitsOverview: (filters: unknown) => ["debits", "overview", filters] as const,
  debitAuthorizations: (filters: unknown) => ["debits", "authorizations", filters] as const,
  debitAttempts: (filters: unknown) => ["debits", "attempts", filters] as const,
  paywayPublicConfig: () => ["payway", "public-config"] as const,
  debitCardQuote: (kind: string, id: string) => ["debits", "card-quote", kind, id] as const,

  // ── §6 Acceso físico e ingreso ────────────────────────────────────────────
  semaphoreRecent: (accessPointId: string) =>
    ["access-semaphore", accessPointId, "recent"] as const,
  accessLogs: (filters: unknown) => ["access-logs", filters] as const,
  accessCounters: (branchId: string | null) => ["access-logs", "counters", branchId] as const,
  agreementsReport: (filters: unknown) => ["access-logs", "agreements-report", filters] as const,
  accessSettings: ["access-settings"] as const,
  accessSnapshot: (locationId: string) => ["access-snapshot", locationId] as const,
  fingerprints: (memberId: string) => ["members", memberId, "fingerprints"] as const,
  // ── Molinete ZKTeco (`backend/src/modules/acceso/zk.routes.ts`) ───────────
  // Todo cuelga de `["zk", …]`: sincronizar, leer o reintentar mueven la fila del equipo,
  // su cola y su revisión a la vez, e invalidar la raíz las refresca juntas.
  zkDevices: (accessPointId?: string | null) => ["zk", "devices", accessPointId ?? null] as const,
  zkOpenTargets: (branchId: string | null) => ["zk", "open-targets", branchId] as const,
  zkOpenCommand: (commandId: string) => ["zk", "open-command", commandId] as const,
  zkCommands: (deviceId: string, status?: string | null) =>
    ["zk", "commands", deviceId, status ?? null] as const,
  zkDeviceUsers: (deviceId: string, match?: string | null) =>
    ["zk", "users", deviceId, match ?? null] as const,
  zkRawLogs: (deviceId: string, limit: number) => ["zk", "raw-logs", deviceId, limit] as const,
  zkWriteMode: (deviceId: string) => ["zk", "write-mode", deviceId] as const,
  zkShadowed: (deviceId: string) => ["zk", "shadowed", deviceId] as const,
  memberZk: (memberId: string) => ["members", memberId, "zk"] as const,
  migrationDay: ["migration", "day"] as const,
  timeclockEntries: (filters: unknown) => ["timeclock-entries", filters] as const,
  myTimeclock: (month: string) => ["timeclock-mine", month] as const,
  myTimeclockNext: ["timeclock-mine", "next"] as const,
  // ── §10 CRM y comunicación ────────────────────────────────────────────────
  prospects: (filters: unknown) => ["prospects", filters] as const,
  prospect: (id: string) => ["prospects", id] as const,
  prospectPipeline: (branchId?: string | null) =>
    ["prospects", "pipeline", branchId ?? null] as const,
  prospectDuplicates: (query: unknown) => ["prospects", "check-duplicates", query] as const,
  tasks: (filters: unknown) => ["tasks", filters] as const,
  communications: (kind: string, id: string, filters?: unknown) =>
    ["people", kind, id, "communications", filters ?? null] as const,
  contactChannels: (kind: string, id: string) => ["people", kind, id, "channels"] as const,
  segments: (branchId?: string | null) => ["segments", branchId ?? null] as const,
  segmentPreview: (query: unknown) => ["segments", "preview", query] as const,
  campaigns: (filters: unknown) => ["campaigns", filters] as const,
  campaign: (id: string) => ["campaigns", id] as const,
  news: (filters: unknown) => ["news", filters] as const,
  wallModeration: (filters: unknown) => ["wall", "moderation", filters] as const,
  conversations: (filters: unknown) => ["conversations", filters] as const,
  conversation: (id: string) => ["conversations", id] as const,
  teacherNotices: (filters: unknown) => ["teachers", "me", "notices", filters] as const,
  classSessions: (filters: unknown) => ["class-sessions", filters] as const,
} as const;
